import { Utilities } from "./utilities.js";
import { WindowMetrics } from "./window-metrics.js";

let
    // These determine how flick animations behave when the flicked image
    // hits a viewport boundary
    rubberBandModes = {
        // The animation is capped at the boundary & nothing happens
        disabled: 0,

        // The animation rubber-bands where it hits boundaries in
        // the x and y dimensions, independently
        independentAxes: 1,

        // The animation rubber-bands where it hits any boundary along
        // the path of motion - this generally feels a little less hectic
        // than the "independentAxes" mode, but it also makes it kind of
        // feel like things are getting "snagged" at the edge of the
        // screen; for this site we're not currently using this mode
        vector: 2
    },

    settings = {
        // Fullscreen animation settings
        zoomInDuration: 0.5,
        zoomOutDuration: 0.25,

        // Minimum pixels per second to trigger a flick gesture
        minVelocity: 500.0,

        // Rate of deceleration of the flick animation, in
        // pixels per second squared
        deceleration: -2000.0,

        // Maximum duration of a flick animation
        maxTimeToRest: 1.0,

        // Minimum rate between move and release events for velocity
        // to be considered
        minSampleInterval: 50,

        // Maximum distance out of bounds the image can move
        rubberBandMaxDistance: 100,

        // Maximum duration of the time for the flick to finish after
        // going out of bounds, in seconds
        rubberBandMaxTime: 0.5,

        // Minimum time for the rubber band motion to come to rest
        // at its final position - this prevents things from looking
        // too jerky
        rubberBandMinResetTime: 0.5,

        // The time to snap into position after dragging the image
        // out of bounds but not causing a flick
        outOfBoundsResetSpeed: 1000,
        outOfBoundsMinResetTime: 0.25,
        outOfBoundsMaxResetTime: 1.0
    };

export class ZoomableImage {
    constructor(container, flickEnabled, rubberBandMode) {
        let t = this;

        t.container = container;
        t.flickEnabled = flickEnabled;
        t.rubberBandMode = rubberBandMode;
        t.smallImage = t.container.querySelector("img");
        t.dX = 0;
        t.dY = 0;

        t.container.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            // Only allow this in mobile mode
            if (!WindowMetrics.isMobile())
                return;

            // Determine where the user clicked and center the image on that point
            let bounds = t.smallImage.getBoundingClientRect(),
                localX = event.clientX,
                localY = event.clientY;

            if (localX == undefined)
                localX = event.touches[0].clientX;
            if (localY == undefined)
                localY = event.touches[0].clientY;

            // Record the raw screen coordinates while we're at it
            t.clickPointX = localX;
            t.clickPointY = localY;

            localX -= bounds.left;
            localY -= bounds.top;

            t.localPercentX = localX / t.smallImage.clientWidth;
            t.localPercentY = localY / t.smallImage.clientHeight;

            if (t.localPercentX < 0)
                t.localPercentX = 0;
            else if (t.localPercentX > 1)
                t.localPercentX = 1;

            if (t.localPercentY < 0)
                t.localPercentY = 0;
            else if (t.localPercentY > 1)
                t.localPercentY = 1;

            // Get this before locking scrolling - that affects the values
            // on some browsers
            bounds = t.smallImage.getBoundingClientRect();

            // Create backdrop
            t.backdrop = Utilities.createElementFromTemplate(`<div class="zoomable-image-backdrop"></div>`);
            document.body.appendChild(t.backdrop);

            // Create viewport
            t.viewport = Utilities.createElementFromTemplate(`<div class="zoomable-image-viewport"></div>`);
            document.body.appendChild(t.viewport);

            // Create image
            t.dragImage = new Image();
            t.dragImage.src = t.smallImage.src;
            t.dragImage.className = "zoomable-image-drag";
            document.body.appendChild(t.dragImage);

            // Create close button
            t.closeButton = Utilities.createElementFromTemplate(`<button class="zoomable-image-close-button">Close</button>`);
            document.body.appendChild(t.closeButton);

            t.closeCallback = function (event) {
                event.preventDefault();
                event.stopPropagation();

                // Exit zoomed mode
                t.close();
            };

            t.backdrop.addEventListener("click", t.closeCallback);
            t.closeButton.addEventListener("click", t.closeCallback);

            // Setup resize callback
            t.resizeCallback = () => {
                if (WindowMetrics.isMobile()) {
                    // Make sure t.imageLeftPosition and t.imageTopPosition are within the desired range
                    adjustTopLeftPosition.call(t);

                    // Update the image position
                    t.dragImage.style.left = `${t.imageLeftPosition}px`;
                    t.dragImage.style.top = `${t.imageTopPosition}px`;
                } else {
                    // We've switched to desktop - exit zoomed mode
                    window.removeEventListener("resize", t.resizeCallback);
                    t.close();
                }
            };

            window.addEventListener("resize", t.resizeCallback);

            // Setup dragging
            setupDragCallback.call(t);

            // Lock scrolling
            Utilities.lockScrolling(t.viewport);

            // Wait until the image has loaded to continue
            let
                loaded = false,
                loadCallback = function () {
                    if (loaded)
                        return;

                    if (t.dragImage.clientWidth == 0)
                        return;

                    loaded = true;

                    // If there's a "@2x" somewhere in the file name,
                    // display it at half size
                    if (t.dragImage.src.indexOf("@2x") >= 0) {
                        t.dragImage.width /= 2;
                        t.imageScale = 2;
                    } else {
                        t.imageScale = 1;
                    }

                    // Determine the final (zoomed in) image size & position - initially we
                    // assume that we'll move the image to the calculated offset
                    let centerPointX = t.localPercentX * t.dragImage.clientWidth,
                        centerPointY = t.localPercentY * t.dragImage.clientHeight;

                    t.imageLeftPosition = t.clickPointX + (t.dragImage.clientWidth / 2) - centerPointX;
                    t.imageTopPosition = t.clickPointY - centerPointY;

                    // Make sure t.imageLeftPosition and t.imageTopPosition are within the desired range
                    adjustTopLeftPosition.call(t);

                    // Determine initial (zoomed out) image size & position
                    let yDelta = t.imageTopPosition - (bounds.top + (bounds.height / 2) - (t.dragImage.clientHeight / 2)),
                        xDelta = t.imageLeftPosition - (bounds.left + (bounds.width / 2)),
                        scaleDelta = 1 - (t.smallImage.clientWidth / t.dragImage.clientWidth),
                        animationState = {
                            percentComplete: 0
                        };

                    // Show the image
                    t.dragImage.style.visibility = "visible";

                    // Animate the transition
                    let timeline = new TimelineMax(),
                        tickCallback = function () {
                            let top = t.imageTopPosition - ((1 - animationState.percentComplete) * yDelta);
                            t.dragImage.style.top = `${Math.round(top * 1000) / 1000}px`;

                            let left = t.imageLeftPosition - ((1 - animationState.percentComplete) * xDelta);
                            t.dragImage.style.left = `${Math.round(left * 1000) / 1000}px`;

                            let scale = 1 - ((1 - animationState.percentComplete) * scaleDelta);
                            t.dragImage.style.transform = `translate(-50%, 0%) scale(${Math.round(scale * 1000) / 1000})`;
                        };

                    tickCallback();

                    // Fade in the backdrop & button
                    timeline.to(
                        t.backdrop,
                        settings.zoomInDuration,
                        {
                            ease: Power3.easeOut,
                            opacity: 1
                        },
                        0);

                    timeline.to(
                        t.closeButton,
                        settings.zoomInDuration,
                        {
                            ease: Power3.easeOut,
                            opacity: 1
                        },
                        0);

                    // Zoom in the image and shift it into place
                    timeline.to(
                        animationState,
                        settings.zoomInDuration,
                        {
                            ease: Power3.easeOut,
                            percentComplete: 1,
                            onUpdate: tickCallback
                        },
                        0);
                };

            t.dragImage.addEventListener("load", loadCallback);
            loadCallback();
        });
    }

    close() {
        let t = this;

        t.backdrop.removeEventListener("click", t.closeCallback);
        t.closeButton.removeEventListener("click", t.closeCallback);

        // Fade everything out
        TweenMax.fromTo(
            [
                t.backdrop,
                t.dragImage,
                t.closeButton
            ],
            settings.zoomOutDuration,
            {
                opacity: 1
            },
            {
                ease: Power1.easeOut,
                opacity: 0,
                onComplete: function () {
                    // Remove resize callback
                    window.removeEventListener("resize", t.resizeCallback);

                    // Unlock scrolling
                    Utilities.unlockScrolling(t.viewport);

                    // Remove everything from the DOM
                    document.body.removeChild(t.backdrop);
                    document.body.removeChild(t.viewport);
                    document.body.removeChild(t.dragImage);
                    document.body.removeChild(t.closeButton);

                    // Reset drag offsets
                    t.dX = 0;
                    t.dY = 0;
                }
            });
    }

    static get rubberBandModes() {
        return rubberBandModes;
    }
}

// Private methods
let
    // Adjusts the default zoomed image positioning -"t.imageLeftPosition" and
    // "t.imageTopPosition" - to make sure the image is within our viewport's bounds
    adjustTopLeftPosition = function () {
        let t = this,
            halfImageWidth = t.dragImage.clientWidth / 2;

        // Make sure the t.imageLeftPosition value is within the desired range
        if (t.dragImage.clientWidth > t.viewport.clientWidth) {
            // The image is wider than the viewport
            let leftEdge = t.imageLeftPosition - halfImageWidth + t.dX;
            if (leftEdge > t.viewport.offsetLeft) {
                t.imageLeftPosition = t.viewport.offsetLeft + halfImageWidth - t.dX;
            } else {
                let rightEdge = t.imageLeftPosition + halfImageWidth + t.dX;
                if (rightEdge < t.viewport.offsetLeft + t.viewport.clientWidth)
                    t.imageLeftPosition = t.viewport.offsetLeft + t.viewport.clientWidth - halfImageWidth - t.dX;
            }
        } else {
            // The image is narrower than the viewport - make sure it's in the viewport's bounds
            let leftEdge = t.imageLeftPosition - halfImageWidth + t.dX;
            if (leftEdge < t.viewport.offsetLeft) {
                t.imageLeftPosition = t.viewport.offsetLeft + halfImageWidth - t.dX;
            } else {
                let rightEdge = t.imageLeftPosition + halfImageWidth + t.dX;
                if (rightEdge > t.viewport.offsetLeft + t.viewport.clientWidth)
                    t.imageLeftPosition = t.viewport.offsetLeft + t.viewport.clientWidth - halfImageWidth - t.dX;
            }
        }

        // Make sure the t.imageTopPosition value is within the desired range
        if (t.dragImage.clientHeight > t.viewport.clientHeight) {
            // The image is taller than the viewport
            let topEdge = t.imageTopPosition + t.dY;
            if (topEdge > t.viewport.offsetTop) {
                t.imageTopPosition = t.viewport.offsetTop - t.dY;
            } else {
                let bottomEdge = t.imageTopPosition + t.dragImage.clientHeight + t.dY;
                if (bottomEdge < t.viewport.offsetTop + t.viewport.clientHeight)
                    t.imageTopPosition = t.viewport.offsetTop + t.viewport.clientHeight - t.dragImage.clientHeight - t.dY;
            }
        } else {
            // The image is shorter than the viewport - make sure it's in the viewport's bounds
            let topEdge = t.imageTopPosition + t.dY;
            if (topEdge < t.viewport.offsetTop) {
                t.imageTopPosition = t.viewport.offsetTop - t.dY;
            } else {
                let bottomEdge = t.imageTopPosition + t.dragImage.clientHeight + t.dY;
                if (bottomEdge > t.viewport.offsetTop + t.viewport.clientHeight)
                    t.imageTopPosition = t.viewport.offsetTop + t.viewport.clientHeight - t.dragImage.clientHeight - t.dY;
            }
        }
    },

    // Checks a proposed X offset to be used in a translation from the default
    // "t.imageLeftPosition" value - if the offset puts the image out of bounds,
    // an adjusted value is returned
    determineCappedDragOffsetX = function (proposedDX) {
        let t = this,
            halfImageWidth = t.dragImage.clientWidth / 2;

        if (t.dragImage.clientWidth > t.viewport.clientWidth) {
            // The image is wider than the viewport
            let leftEdge = t.imageLeftPosition - halfImageWidth + proposedDX;
            if (leftEdge > t.viewport.offsetLeft) {
                return t.viewport.offsetLeft - (t.imageLeftPosition - halfImageWidth);
            } else {
                let rightEdge = t.imageLeftPosition + halfImageWidth + proposedDX;
                if (rightEdge < t.viewport.offsetLeft + t.viewport.clientWidth)
                    return t.viewport.offsetLeft + t.viewport.clientWidth - (t.imageLeftPosition + halfImageWidth);
            }
        } else {
            // The image is narrower than the viewport
            let leftEdge = t.imageLeftPosition - halfImageWidth + proposedDX,
                rightEdge = leftEdge + t.dragImage.clientWidth;
            if (leftEdge < t.viewport.offsetLeft) {
                if (rightEdge < t.viewport.offsetLeft + t.viewport.clientWidth)
                    return t.viewport.offsetLeft - (t.imageLeftPosition - halfImageWidth);
            } else if (rightEdge > t.viewport.offsetLeft + t.viewport.clientWidth) {
                if (leftEdge > t.viewport.offsetLeft)
                    return t.viewport.offsetLeft + t.viewport.clientWidth - (t.imageLeftPosition + halfImageWidth);
            }
        }

        // The proposed value is ok
        return proposedDX;
    },

    // Checks a proposed Y offset to be used in a translation from the default
    // "t.imageTopPosition" value - if the offset puts the image out of bounds,
    // an adjusted value is returned
    determineCappedDragOffsetY = function (proposedDY) {
        let t = this;

        if (t.dragImage.clientHeight > t.viewport.clientHeight) {
            // The image is taller than the viewport
            let topEdge = t.imageTopPosition + proposedDY;
            if (topEdge > t.viewport.offsetTop) {
                return t.viewport.offsetTop - t.imageTopPosition;
            } else {
                let bottomEdge = t.imageTopPosition + t.dragImage.clientHeight + proposedDY;
                if (bottomEdge < t.viewport.offsetTop + t.viewport.clientHeight)
                    return t.viewport.offsetTop + t.viewport.clientHeight - (t.imageTopPosition + t.dragImage.clientHeight);
            }
        } else {
            // The image is shorter than the viewport
            let topEdge = t.imageTopPosition + proposedDY,
                bottomEdge = topEdge + t.dragImage.clientHeight;
            if (topEdge < t.viewport.offsetTop) {
                if (bottomEdge < t.viewport.offsetTop + t.viewport.clientHeight)
                    return t.viewport.offsetTop - t.imageTopPosition;
            } else if (bottomEdge > t.viewport.offsetTop + t.viewport.clientHeight) {
                if (topEdge > t.viewport.offsetTop)
                    return t.viewport.offsetTop + t.viewport.clientHeight - (t.imageTopPosition + t.dragImage.clientHeight);
            }
        }

        // The proposed value is ok
        return proposedDY;
    },

    // Calculates the time at which a proposed flick animation will intersect
    // with a viewport boundary
    calculateViewportIntersectionVelocityAndTime = function (proposedDistance, cappedDistance, initialVelocity, deceleration) {
        let hittingBounds = Math.abs(proposedDistance - cappedDistance) > 0.0001;
        if (!hittingBounds)
            return null;

        if (deceleration == 0)
            return null;

        // vf^2 = v0^2 + 2ad
        // vf = Math.sqrt(v0^2 + 2ad)
        let intersectVelocity = Math.sqrt((initialVelocity * initialVelocity) + (2 * deceleration * cappedDistance));
        if (initialVelocity < 0 && intersectVelocity > 0)
            intersectVelocity = -intersectVelocity;

        // vf = v0 + at
        // t = (vf - v0) / a
        let intersectTime = (intersectVelocity - initialVelocity) / deceleration;

        return {
            velocity: intersectVelocity,
            time: intersectTime
        };
    },

    // This produces all of the information necessary to produce a basic
    // flick animation
    calculateFlickAnimationSettings = function (
        originX,
        originY,
        velocityX,
        velocityY,
        velocityMagnitude,
        deceleration) {
        let
            t = this,

            totalTime = velocityMagnitude / Math.abs(deceleration),
            //angle = velocityX < 0.00001 && velocityX > -0.00001 ? Math.PI / 2 : Math.atan(velocityY / velocityX),
            angle = Math.atan(velocityY / velocityX),
            cosAngle = Math.cos(angle),
            sinAngle = Math.sin(angle);

        if (Math.abs(cosAngle) < 0.0001)
            cosAngle = 0;
        if (Math.abs(sinAngle) < 0.0001)
            sinAngle = 0;

        // Adjust the decceleration rate so that the flick comes to rest
        // within the specified amount of time
        if (totalTime > settings.maxTimeToRest) {
            totalTime = settings.maxTimeToRest;
            deceleration = -velocityMagnitude / totalTime;
        }

        // Calculate the velocity and acceleration along the x and y axes
        let
            decelerationX = cosAngle * Math.abs(deceleration),
            decelerationY = sinAngle * Math.abs(deceleration);

        if (velocityX < 0 && decelerationX < 0 || velocityX > 0 && decelerationX > 0)
            decelerationX = -decelerationX;
        if (velocityY < 0 && decelerationY < 0 || velocityY > 0 && decelerationY > 0)
            decelerationY = -decelerationY;

        // Determine if the image will intersect the viewport bounds during
        // the flick, and if so, at what time the intersection occurs
        let proposedDistanceX = (velocityX * totalTime) + (0.5 * decelerationX * totalTime * totalTime),
            cappedDistanceX = determineCappedDragOffsetX.call(t, proposedDistanceX + originX),

            proposedDistanceY = (velocityY * totalTime) + (0.5 * decelerationY * totalTime * totalTime),
            cappedDistanceY = determineCappedDragOffsetY.call(t, proposedDistanceY + originY);

        // From now on, we'll want the distance from our origin point and not
        // an absolute value
        cappedDistanceX -= originX;
        cappedDistanceY -= originY;

        // Determine if the image will intersect the viewport bounds during
        // the flick, and if so, at what time the intersection occurs
        let
            intersectVelocityAndTimeX = calculateViewportIntersectionVelocityAndTime(
                proposedDistanceX,
                cappedDistanceX,
                velocityX,
                decelerationX),

            intersectVelocityAndTimeY = calculateViewportIntersectionVelocityAndTime(
                proposedDistanceY,
                cappedDistanceY,
                velocityY,
                decelerationY),

            rubberBandStartVelocityAndTime;

        if (intersectVelocityAndTimeX !== null) {
            // We're intersecting the viewport bounds horizontally
            if (intersectVelocityAndTimeY !== null) {
                // We're intersecting the viewport bounds both horizontally
                // and vertically - choose whichever happens first
                if (intersectVelocityAndTimeX.time < intersectVelocityAndTimeY.time)
                    rubberBandStartVelocityAndTime = intersectVelocityAndTimeX;
                else
                    rubberBandStartVelocityAndTime = intersectVelocityAndTimeY;
            } else {
                // We're only intersecting the viewport bounds horizontally
                rubberBandStartVelocityAndTime = intersectVelocityAndTimeX;
            }
        } else {
            if (intersectVelocityAndTimeY !== null) {
                // We're only intersecting the viewport bounds vertically
                rubberBandStartVelocityAndTime = intersectVelocityAndTimeY;
            } else {
                // We're not intersecting the viewport bounds at all
                rubberBandStartVelocityAndTime = null;
            }
        }

        return {
            originX: originX,
            originY: originY,
            velocityX: velocityX,
            velocityY: velocityY,
            velocityMagnitude: velocityMagnitude,
            deceleration: deceleration,

            totalTime: totalTime,
            cosAngle: cosAngle,
            sinAngle: sinAngle,

            decelerationX: decelerationX,
            decelerationY: decelerationY,

            cappedDistanceX: cappedDistanceX,
            cappedDistanceY: cappedDistanceY,

            intersectVelocityAndTimeX: intersectVelocityAndTimeX,
            intersectVelocityAndTimeY: intersectVelocityAndTimeY,
            rubberBandStartVelocityAndTime: rubberBandStartVelocityAndTime
        };
    },

    // This produces all of the information necessary to produce a rubber band
    // animation along an arbitrary vector defined by x and y velocities
    calculateRubberBandAnimationSettings = function (flickSettings) {
        let
            // The total length to come to a stop for this flick - this is a magnitude and always
            // a positive value
            totalDistance = (flickSettings.velocityMagnitude * flickSettings.totalTime) + (0.5 * flickSettings.deceleration * flickSettings.totalTime * flickSettings.totalTime);

        // There's no rubber band animation to be made if this is null
        if (flickSettings.rubberBandStartVelocityAndTime == null)
            return flickSettings;

        let
            // The distance into the flick at which we intersect a viewport boundary
            rubberBandStartTime = flickSettings.rubberBandStartVelocityAndTime.time,
            rubberBandStartDistance = (flickSettings.velocityMagnitude * rubberBandStartTime) + (0.5 * flickSettings.deceleration * rubberBandStartTime * rubberBandStartTime),
            rubberBandRestingPositionOffsetX,
            rubberBandRestingPositionOffsetY;

        // When the rubber band starts out of bounds, the entire animation is the rubber band
        // and we'll be ending at a location behind our starting point
        if (rubberBandStartDistance < 0) {
            rubberBandRestingPositionOffsetX = flickSettings.cosAngle * rubberBandStartDistance;
            if (flickSettings.velocityX > 0 && rubberBandRestingPositionOffsetX > 0 || flickSettings.velocityX < 0 && rubberBandRestingPositionOffsetX < 0)
                rubberBandRestingPositionOffsetX = -rubberBandRestingPositionOffsetX;

            rubberBandRestingPositionOffsetY = flickSettings.sinAngle * rubberBandStartDistance;
            if (flickSettings.velocityY > 0 && rubberBandRestingPositionOffsetY > 0 || flickSettings.velocityY < 0 && rubberBandRestingPositionOffsetY < 0)
                rubberBandRestingPositionOffsetY = -rubberBandRestingPositionOffsetY;

            rubberBandStartDistance = 0;
        } else {
            rubberBandRestingPositionOffsetX = 0;
            rubberBandRestingPositionOffsetY = 0;
        }

        let
            // The velocity of the animation at the time of the boundary intersection
            rubberBandStartVelocity = Math.sqrt((flickSettings.velocityMagnitude * flickSettings.velocityMagnitude) + (2 * flickSettings.deceleration * rubberBandStartDistance)),

            // The time between the intersection and coming to a rest
            rubberBandStretchDuration = flickSettings.totalTime - rubberBandStartTime,

            // The distance traveled beyond the boundary intersection point
            distanceBeyondBounds = totalDistance - rubberBandStartDistance,

            // The deceleration required to come to a rest given the distance & time beyond the intersection
            rubberBandDeceleration = flickSettings.deceleration;

        // Adjust total flick distance and the deceleration & deceleration time beyond the intersection
        // point so that we come to a rest within the desired distance
        if (distanceBeyondBounds > settings.rubberBandMaxDistance) {
            // The flick is sending the image beyond our maximum rubber band
            // distance - cap it
            rubberBandDeceleration = -(rubberBandStartVelocity * rubberBandStartVelocity) / (2 * settings.rubberBandMaxDistance);
            rubberBandStretchDuration = rubberBandStartVelocity / Math.abs(rubberBandDeceleration);
            totalDistance = rubberBandStartDistance + settings.rubberBandMaxDistance;
            distanceBeyondBounds = totalDistance - rubberBandStartDistance;
        }

        // Now that the distance and decelaration of the rubber band has been determined,
        // calculate its duration
        let rubberBandRestDuration = rubberBandStretchDuration >= settings.rubberBandMinResetTime ? rubberBandStretchDuration : settings.rubberBandMinResetTime;

        // Add all of the base flick values to a new object
        let rubberBandSettings = {};
        Object.assign(rubberBandSettings, flickSettings);

        // Add all of the rubber band -specific values to the object & return it
        return Object.assign(
            rubberBandSettings,
            {
                totalTime: rubberBandStartTime
                    + rubberBandStretchDuration
                    + rubberBandRestDuration,

                rubberBandStartVelocity: rubberBandStartVelocity,

                rubberBandStartTime: rubberBandStartTime,
                rubberBandStretchDuration: rubberBandStretchDuration,
                rubberBandRestDuration: rubberBandRestDuration,

                totalDistance: totalDistance,
                rubberBandStartDistance: rubberBandStartDistance,
                distanceBeyondBounds: distanceBeyondBounds,

                rubberBandRestingPositionOffsetX: rubberBandRestingPositionOffsetX,
                rubberBandRestingPositionOffsetY: rubberBandRestingPositionOffsetY,

                rubberBandDeceleration: rubberBandDeceleration
            });
    },

    // This creates the animation for the "vector" rubber band mode
    setupVectorRubberBandAnimation = function (rubberBandSettings) {
        let
            t = this,

            animationState = {
                elapsedTime: 0
            };

        t.flickTimeline.fromTo(
            animationState,
            rubberBandSettings.totalTime,
            {
                elapsedTime: 0,
            },
            {
                ease: Power0.easeNone,
                elapsedTime: rubberBandSettings.totalTime,
                onUpdate: function () {
                    // Determine which portion of the animation we're in
                    let distanceAlongFlickPath,
                        offsetX = 0,
                        offsetY = 0;

                    if (animationState.elapsedTime < rubberBandSettings.rubberBandStartTime) {
                        // We're animating toward the boundary intersection point
                        distanceAlongFlickPath = (rubberBandSettings.velocityMagnitude * animationState.elapsedTime) + (0.5 * rubberBandSettings.deceleration * animationState.elapsedTime * animationState.elapsedTime);
                    } else if (animationState.elapsedTime < rubberBandSettings.rubberBandStartTime + rubberBandSettings.rubberBandStretchDuration) {
                        // We're rubber banding beyond the bondary intersection point
                        let elapsedTimeStep2 = animationState.elapsedTime - rubberBandSettings.rubberBandStartTime;

                        distanceAlongFlickPath = rubberBandSettings.rubberBandStartDistance + (rubberBandSettings.rubberBandStartVelocity * elapsedTimeStep2) + (0.5 * rubberBandSettings.rubberBandDeceleration * elapsedTimeStep2 * elapsedTimeStep2);
                    } else {
                        // We're coming to a rest at our final position
                        let elapsedTimeStep3 = animationState.elapsedTime - (rubberBandSettings.rubberBandStartTime + rubberBandSettings.rubberBandStretchDuration),
                            percentComplete = elapsedTimeStep3 / rubberBandSettings.rubberBandRestDuration;

                        if (percentComplete < 0)
                            percentComplete = 0;
                        else if (percentComplete > 1)
                            percentComplete = 1;

                        percentComplete = Power2.easeInOut.getRatio(percentComplete);
                        distanceAlongFlickPath = rubberBandSettings.rubberBandStartDistance + ((1 - percentComplete) * rubberBandSettings.distanceBeyondBounds);

                        offsetX = percentComplete * rubberBandSettings.rubberBandRestingPositionOffsetX;
                        offsetY = percentComplete * rubberBandSettings.rubberBandRestingPositionOffsetY;
                    }


                    // Update our flick distance values
                    t.flickDistanceX = rubberBandSettings.cosAngle * distanceAlongFlickPath;
                    if (rubberBandSettings.velocityX < 0 && t.flickDistanceX > 0 || rubberBandSettings.velocityX > 0 && t.flickDistanceX < 0)
                        t.flickDistanceX = -t.flickDistanceX;

                    t.flickDistanceX += offsetX;

                    t.flickDistanceY = rubberBandSettings.sinAngle * distanceAlongFlickPath;
                    if (rubberBandSettings.velocityY < 0 && t.flickDistanceY > 0 || rubberBandSettings.velocityY > 0 && t.flickDistanceY < 0)
                        t.flickDistanceY = -t.flickDistanceY;

                    t.flickDistanceY += offsetY;

                    // Update the transformation
                    t.dragImage.style.transform = `translate(calc(-50% + ${t.dX + t.flickDistanceX}px), ${t.dY + t.flickDistanceY}px)`;
                }
            });
    },

    // This creates the animation for the "independentAxes" rubber band mode
    setupIndependentAxesRubberBandAnimation = function (rubberBandSettingsX, rubberBandSettingsY) {
        let t = this,

            animationState = {
                elapsedTimeX: 0,
                elapsedTimeY: 0
            };

        if (rubberBandSettingsX.rubberBandStartVelocityAndTime != null) {
            t.flickTimeline.fromTo(
                animationState,
                rubberBandSettingsX.totalTime,
                {
                    elapsedTimeX: 0,
                },
                {
                    ease: Power0.easeNone,
                    elapsedTimeX: rubberBandSettingsX.totalTime,
                    onUpdate: function () {
                        // Determine which portion of the animation we're in
                        let distanceAlongFlickPath,
                            offsetX = 0;

                        if (animationState.elapsedTimeX < rubberBandSettingsX.rubberBandStartTime) {
                            // We're animating toward the boundary intersection point
                            distanceAlongFlickPath = (rubberBandSettingsX.velocityMagnitude * animationState.elapsedTimeX) + (0.5 * rubberBandSettingsX.deceleration * animationState.elapsedTimeX * animationState.elapsedTimeX);
                        } else if (animationState.elapsedTimeX < rubberBandSettingsX.rubberBandStartTime + rubberBandSettingsX.rubberBandStretchDuration) {
                            // We're rubber banding beyond the bondary intersection point
                            let elapsedTimeStep2 = animationState.elapsedTimeX - rubberBandSettingsX.rubberBandStartTime;

                            distanceAlongFlickPath = rubberBandSettingsX.rubberBandStartDistance + (rubberBandSettingsX.rubberBandStartVelocity * elapsedTimeStep2) + (0.5 * rubberBandSettingsX.rubberBandDeceleration * elapsedTimeStep2 * elapsedTimeStep2);
                        } else {
                            // We're coming to a rest at our final position
                            let elapsedTimeStep3 = animationState.elapsedTimeX - (rubberBandSettingsX.rubberBandStartTime + rubberBandSettingsX.rubberBandStretchDuration),
                                percentComplete = elapsedTimeStep3 / rubberBandSettingsX.rubberBandRestDuration;

                            if (percentComplete < 0)
                                percentComplete = 0;
                            else if (percentComplete > 1)
                                percentComplete = 1;

                            percentComplete = Power2.easeInOut.getRatio(percentComplete);
                            distanceAlongFlickPath = rubberBandSettingsX.rubberBandStartDistance + ((1 - percentComplete) * rubberBandSettingsX.distanceBeyondBounds);


                            offsetX = percentComplete * rubberBandSettingsX.rubberBandRestingPositionOffsetX;
                        }

                        // Update our flick distance values
                        t.flickDistanceX = rubberBandSettingsX.cosAngle * distanceAlongFlickPath;
                        if (rubberBandSettingsX.velocityX < 0 && t.flickDistanceX > 0 || rubberBandSettingsX.velocityX > 0 && t.flickDistanceX < 0)
                            t.flickDistanceX = -t.flickDistanceX;

                        t.flickDistanceX += offsetX;
                    }
                },
                0);
        } else {
            t.flickTimeline.fromTo(
                animationState,
                rubberBandSettingsX.totalTime,
                {
                    elapsedTimeX: 0,
                },
                {
                    ease: Power0.easeNone,
                    elapsedTimeX: rubberBandSettingsX.totalTime,
                    onUpdate: function () {
                        // Update our flick distance values
                        let distanceAlongFlickPath = (rubberBandSettingsX.velocityMagnitude * animationState.elapsedTimeX) + (0.5 * rubberBandSettingsX.deceleration * animationState.elapsedTimeX * animationState.elapsedTimeX),
                            percentComplete = animationState.elapsedTimeX / rubberBandSettingsX.totalTime,
                            offsetX = percentComplete * rubberBandSettingsX.rubberBandRestingPositionOffsetX;

                        t.flickDistanceX = rubberBandSettingsX.cosAngle * distanceAlongFlickPath;
                        if (rubberBandSettingsX.velocityX < 0 && t.flickDistanceX > 0 || rubberBandSettingsX.velocityX > 0 && t.flickDistanceX < 0)
                            t.flickDistanceX = -t.flickDistanceX;

                        //t.flickDistanceX += offsetX;

                        // Cap the value if necessary
                        if (rubberBandSettingsX.velocityX >= 0) {
                            if (t.flickDistanceX > rubberBandSettingsX.cappedDistanceX)
                                t.flickDistanceX = rubberBandSettingsX.cappedDistanceX;
                        } else {
                            if (t.flickDistanceX < rubberBandSettingsX.cappedDistanceX)
                                t.flickDistanceX = rubberBandSettingsX.cappedDistanceX;
                        }
                    }
                },
                0);
        }

        if (rubberBandSettingsY.rubberBandStartVelocityAndTime != null) {
            t.flickTimeline.fromTo(
                animationState,
                rubberBandSettingsY.totalTime,
                {
                    elapsedTimeY: 0,
                },
                {
                    ease: Power0.easeNone,
                    elapsedTimeY: rubberBandSettingsY.totalTime,
                    onUpdate: function () {
                        // Determine which portion of the animation we're in
                        let distanceAlongFlickPath,
                            offsetY = 0;

                        if (animationState.elapsedTimeY < rubberBandSettingsY.rubberBandStartTime) {
                            // We're animating toward the boundary intersection point
                            distanceAlongFlickPath = (rubberBandSettingsY.velocityMagnitude * animationState.elapsedTimeY) + (0.5 * rubberBandSettingsY.deceleration * animationState.elapsedTimeY * animationState.elapsedTimeY);
                        } else if (animationState.elapsedTimeY < rubberBandSettingsY.rubberBandStartTime + rubberBandSettingsY.rubberBandStretchDuration) {
                            // We're rubber banding beyond the bondary intersection point
                            let elapsedTimeStep2 = animationState.elapsedTimeY - rubberBandSettingsY.rubberBandStartTime;

                            distanceAlongFlickPath = rubberBandSettingsY.rubberBandStartDistance + (rubberBandSettingsY.rubberBandStartVelocity * elapsedTimeStep2) + (0.5 * rubberBandSettingsY.rubberBandDeceleration * elapsedTimeStep2 * elapsedTimeStep2);
                        } else {
                            // We're coming to a rest at our final position
                            let elapsedTimeStep3 = animationState.elapsedTimeY - (rubberBandSettingsY.rubberBandStartTime + rubberBandSettingsY.rubberBandStretchDuration),
                                percentComplete = elapsedTimeStep3 / rubberBandSettingsY.rubberBandRestDuration;

                            if (percentComplete < 0)
                                percentComplete = 0;
                            else if (percentComplete > 1)
                                percentComplete = 1;

                            percentComplete = Power2.easeInOut.getRatio(percentComplete);
                            distanceAlongFlickPath = rubberBandSettingsY.rubberBandStartDistance + ((1 - percentComplete) * rubberBandSettingsY.distanceBeyondBounds);

                            offsetY = percentComplete * rubberBandSettingsY.rubberBandRestingPositionOffsetY;
                        }

                        // Update our flick distance values
                        t.flickDistanceY = rubberBandSettingsY.sinAngle * distanceAlongFlickPath;
                        if (rubberBandSettingsY.velocityX < 0 && t.flickDistanceY > 0 || rubberBandSettingsY.velocityX > 0 && t.flickDistanceY < 0)
                            t.flickDistanceY = -t.flickDistanceY;

                        t.flickDistanceY += offsetY;
                    }
                },
                0);
        } else {
            t.flickTimeline.fromTo(
                animationState,
                rubberBandSettingsY.totalTime,
                {
                    elapsedTimeY: 0,
                },
                {
                    ease: Power0.easeNone,
                    elapsedTimeY: rubberBandSettingsY.totalTime,
                    onUpdate: function () {
                        // Update our flick distance values
                        let distanceAlongFlickPath = (rubberBandSettingsY.velocityMagnitude * animationState.elapsedTimeY) + (0.5 * rubberBandSettingsY.deceleration * animationState.elapsedTimeY * animationState.elapsedTimeY),
                            percentComplete = animationState.elapsedTimeY / rubberBandSettingsY.totalTime,
                            offsetY = percentComplete * rubberBandSettingsY.rubberBandRestingPositionOffsetY;

                        t.flickDistanceY = rubberBandSettingsY.sinAngle * distanceAlongFlickPath;
                        if (rubberBandSettingsY.velocityX < 0 && t.flickDistanceX > 0 || rubberBandSettingsY.velocityX > 0 && t.flickDistanceX < 0)
                            t.flickDistanceY = -t.flickDistanceY;

                        //t.flickDistanceY += offsetY;

                        // Cap the value if necessary
                        if (rubberBandSettingsY.velocityY >= 0) {
                            if (t.flickDistanceY > rubberBandSettingsY.cappedDistanceY)
                                t.flickDistanceY = rubberBandSettingsY.cappedDistanceY;
                        } else {
                            if (t.flickDistanceY < rubberBandSettingsY.cappedDistanceY)
                                t.flickDistanceY = rubberBandSettingsY.cappedDistanceY;
                        }
                    }
                },
                0);
        }

        let tickCallback = function () {
            if (t.flickTimeline != null) {
                // Update the transformation
                t.dragImage.style.transform = `translate(calc(-50% + ${t.dX + t.flickDistanceX}px), ${t.dY + t.flickDistanceY}px)`;
            } else {
                TweenMax.ticker.removeEventListener("tick", tickCallback);
            }
        };

        TweenMax.ticker.addEventListener("tick", tickCallback);
    },

    // Sets up the mousedown / touchstart event on the image and all the drag-
    // related events that can happen afterward
    setupDragCallback = function () {
        let
            t = this,

            startCallback = (event) => {
                event.preventDefault();
                event.stopPropagation();

                // Stop any ongoing animation
                if (t.flickTimeline) {
                    t.flickTimeline.kill();
                    t.flickTimeline = null;

                    t.dX += t.flickDistanceX;
                    t.dY += t.flickDistanceY;

                    t.dX = determineCappedDragOffsetX.call(t, t.dX);
                    t.dY = determineCappedDragOffsetY.call(t, t.dY);

                    // Update image position
                    t.dragImage.style.transform = `translate(calc(-50% + ${t.dX}px), ${t.dY}px)`;
                }

                let
                    // The mousedown position that initiated the drag
                    startX = event.clientX,
                    startY = event.clientY,

                    // The current drag position
                    thisX,
                    thisY,

                    // The last position recorded mid-drag
                    // (used for calculating velocity)
                    lastX,
                    lastY,

                    // The new value for t.dX and t.dY, assigned at
                    // the end of the drag
                    newDX = 0,
                    newDY = 0,

                    lastSampleTime = (new Date()).getTime(),
                    velocityX = 0,
                    velocityY = 0;

                if (startX == undefined)
                    startX = event.touches[0].clientX;
                if (startY == undefined)
                    startY = event.touches[0].clientY;

                thisX = startX;
                thisY = startY;
                lastX = thisX;
                lastY = thisY;

                // Add body class
                document.body.classList.add("zoomable-image-dragging");

                // Setup drag event
                let
                    moveCallback = (event) => {
                        //event.preventDefault();
                        event.stopPropagation();

                        // Determine new offset
                        thisX = event.clientX;
                        if (thisX == undefined)
                            thisX = event.touches[0].clientX;

                        thisY = event.clientY;
                        if (thisY == undefined)
                            thisY = event.touches[0].clientY;

                        newDX = thisX - startX + t.dX;
                        newDY = thisY - startY + t.dY;

                        if (t.flickEnabled) {
                            // Update velocity
                            let currentTime = (new Date()).getTime(),
                                dT = currentTime - lastSampleTime;

                            // Filter out any delays in sampling
                            if (dT > settings.minSampleInterval) {
                                velocityX = 0;
                                velocityY = 0;
                                //velocityX = velocityX * 0.9;
                                //velocityY = velocityY * 0.9;
                            } else if (dT > 0) {
                                velocityX = (velocityX * 0.9) + (((thisX - lastX) / dT) * 0.1);
                                velocityY = (velocityY * 0.9) + (((thisY - lastY) / dT) * 0.1);
                            }

                            lastSampleTime = currentTime;
                            lastX = thisX;
                            lastY = thisY;
                        }

                        // Skip this if we allow rubber banding
                        if (t.rubberBandMode == rubberBandModes.disabled) {
                            // Make sure the newDX value is within the desired range
                            newDX = determineCappedDragOffsetX.call(t, newDX);

                            // Make sure the newDY value is within the desired range
                            newDY = determineCappedDragOffsetY.call(t, newDY);
                        }

                        // Update image position
                        t.dragImage.style.transform = `translate(calc(-50% + ${newDX}px), ${newDY}px)`;
                    };

                window.addEventListener("mousemove", moveCallback);
                window.addEventListener("touchmove", moveCallback);

                // Setup finishing event
                let
                    releaseCallback = (event) => {
                        // This causes things to break for some reason,
                        // so let's not do it
                        //event.preventDefault();
                        //event.stopPropagation();

                        t.dX = newDX;
                        t.dY = newDY;

                        window.removeEventListener("mousemove", moveCallback);
                        window.removeEventListener("touchmove", moveCallback);

                        window.removeEventListener("mouseup", releaseCallback);
                        window.removeEventListener("touchend", releaseCallback);

                        document.body.classList.remove("zoomable-image-dragging");

                        // Stop here if we're not configured to perform flick animations
                        if (!t.flickEnabled)
                            return;

                        // Make sure the release event occured within our
                        // minimum sample interval
                        let currentTime = (new Date()).getTime(),
                            dT = currentTime - lastSampleTime;

                        if (dT > settings.minSampleInterval) {
                            velocityX = 0;
                            velocityY = 0;
                        } else {
                            // Convert from pixels/millisecond to pixels/second so that all our
                            // ongoing calculations are in seconds
                            velocityX *= 1000;
                            velocityY *= 1000;
                        }

                        // Determine if the user released the drag with a velocity that
                        // exceeds our minimum for triggering a flick animation
                        let velocityMagnitude = Math.sqrt((velocityX * velocityX) + (velocityY * velocityY));
                        if (velocityMagnitude >= settings.minVelocity) {
                            // We're performing a flick - calculate all of the animation settings
                            // we'll need based on what we know so far
                            let flickSettings = calculateFlickAnimationSettings.call(
                                t,
                                t.dX,
                                t.dY,
                                velocityX,
                                velocityY,
                                velocityMagnitude,
                                settings.deceleration);

                            t.flickDistanceX = 0;
                            t.flickDistanceY = 0;

                            t.flickTimeline = new TimelineMax();

                            // If we've determined that a viewport
                            // intersection will occur, check if we're configured to rubber band
                            if (flickSettings.rubberBandStartVelocityAndTime !== null && t.rubberBandMode != rubberBandModes.disabled) {
                                // Continue based on the specified rubber band mode
                                switch (t.rubberBandMode) {
                                    case rubberBandModes.independentAxes:
                                        let
                                            flickSettingsX = calculateFlickAnimationSettings.call(
                                                t,
                                                t.dX,
                                                t.dY,
                                                velocityX,
                                                0,
                                                Math.abs(velocityX),
                                                settings.deceleration),

                                            flickSettingsY = calculateFlickAnimationSettings.call(
                                                t,
                                                t.dX,
                                                t.dY,
                                                0,
                                                velocityY,
                                                Math.abs(velocityY),
                                                settings.deceleration),

                                            rubberBandSettingsX = calculateRubberBandAnimationSettings(flickSettingsX),
                                            rubberBandSettingsY = calculateRubberBandAnimationSettings(flickSettingsY);

                                        setupIndependentAxesRubberBandAnimation.call(t, rubberBandSettingsX, rubberBandSettingsY);
                                        break;

                                    // rubberBandModes.vector
                                    default:
                                        let rubberBandSettings = calculateRubberBandAnimationSettings(flickSettings);

                                        setupVectorRubberBandAnimation.call(t, rubberBandSettings);
                                        break;
                                }
                            } else {
                                // No viewport intersection was detected or we're not configured
                                // to rubber band - just perform the animation & cap the position at
                                // the viewport bounds if necessary
                                let animationState = {
                                    elapsedTime: 0
                                };

                                t.flickTimeline.to(
                                    animationState,
                                    flickSettings.totalTime,
                                    {
                                        ease: Power0.easeNone,
                                        elapsedTime: flickSettings.totalTime,
                                        onUpdate: function () {
                                            // Update our flick distance values
                                            let distanceAlongFlickPath = (velocityMagnitude * animationState.elapsedTime) + (0.5 * flickSettings.deceleration * animationState.elapsedTime * animationState.elapsedTime);

                                            t.flickDistanceX = flickSettings.cosAngle * distanceAlongFlickPath;
                                            if (velocityX < 0 && t.flickDistanceX > 0 || velocityX > 0 && t.flickDistanceX < 0)
                                                t.flickDistanceX = -t.flickDistanceX;

                                            t.flickDistanceY = flickSettings.sinAngle * distanceAlongFlickPath;
                                            if (velocityY < 0 && t.flickDistanceY > 0 || velocityY > 0 && t.flickDistanceY < 0)
                                                t.flickDistanceY = -t.flickDistanceY;

                                            // Cap the values if necessary
                                            if (velocityX >= 0) {
                                                if (t.flickDistanceX > flickSettings.cappedDistanceX)
                                                    t.flickDistanceX = flickSettings.cappedDistanceX;
                                            } else {
                                                if (t.flickDistanceX < flickSettings.cappedDistanceX)
                                                    t.flickDistanceX = flickSettings.cappedDistanceX;
                                            }

                                            if (velocityY >= 0) {
                                                if (t.flickDistanceY > flickSettings.cappedDistanceY)
                                                    t.flickDistanceY = flickSettings.cappedDistanceY;
                                            } else {
                                                if (t.flickDistanceY < flickSettings.cappedDistanceY)
                                                    t.flickDistanceY = flickSettings.cappedDistanceY;
                                            }

                                            // Update the transformation
                                            t.dragImage.style.transform = `translate(calc(-50% + ${t.dX + t.flickDistanceX}px), ${t.dY + t.flickDistanceY}px)`;
                                        }
                                    });
                            }

                            // Finish up
                            t.flickTimeline.add(function () {
                                t.flickTimeline = null;

                                // Update our current drag offset
                                t.dX += t.flickDistanceX;
                                t.dY += t.flickDistanceY;
                            });
                        } else {
                            // We're not performing a flick - if rubber banding is enabled,
                            // check if the user released the image outside of the viewport's bounds
                            if (t.rubberBandMode != rubberBandModes.disabled) {
                                let cappedDistanceX = determineCappedDragOffsetX.call(t, t.dX),
                                    cappedDistanceY = determineCappedDragOffsetY.call(t, t.dY),
                                    distanceX = cappedDistanceX - t.dX,
                                    distanceY = cappedDistanceY - t.dY;

                                if (Math.abs(distanceX) > 0.0001 || Math.abs(distanceY) > 0.0001) {
                                    // We need to shift the image back into bounds
                                    let distance = Math.sqrt((distanceX * distanceX) + (distanceY * distanceY)),
                                        duration = distance / settings.outOfBoundsResetSpeed;

                                    if (duration < settings.outOfBoundsMinResetTime)
                                        duration = settings.outOfBoundsMinResetTime;
                                    else if (duration > settings.outOfBoundsMaxResetTime)
                                        duration = settings.outOfBoundsMaxResetTime;

                                    t.flickTimeline = new TimelineMax();
                                    t.flickTimeline.to(
                                        t,
                                        duration,
                                        {
                                            ease: Power2.easeOut,
                                            dX: cappedDistanceX,
                                            dY: cappedDistanceY,
                                            onUpdate: function () {
                                                // Update the transformation
                                                t.dragImage.style.transform = `translate(calc(-50% + ${t.dX}px), ${t.dY}px)`;
                                            }
                                        });

                                    // Finish up
                                    t.flickTimeline.add(function () {
                                        t.flickTimeline = null;
                                    });
                                }
                            }
                        }
                    };

                window.addEventListener("mouseup", releaseCallback);
                window.addEventListener("touchend", releaseCallback);
            };

        t.dragImage.addEventListener("mousedown", startCallback);
        t.dragImage.addEventListener("touchstart", startCallback);
    };
